// src/pages/EditableStammdaten.jsx
// Inline-editierbare Stammdaten innerhalb des Protokolls.
// - Auto-Save mit 800ms Debounce
// - Pre-fill aus aktuellem Stationsdaten
// - Modul-spezifische Sicht (Trafo / MS / Station ...)

import { useState, useEffect, useRef } from 'react';
import { useKunde } from '../hooks/useKunden';
import { C, Field, TextInput, NumberInput, Select, Row, Subsection, ComboInput, BezeichnungVorschlaege } from './formui';
import { MS_ANLAGEN, typenFuerMsHersteller, isolierartFuerMsTyp } from '../lib/typenlisten';
import OelWanneBlock, { computeOelwannenBewertung } from './OelWanneBlock';
import ErderanlageBlock from './ErderanlageBlock';
import TrafosEditor from './TrafosEditor';
import { BAUFORM, MS_FELD_TYPEN, US_OHNE_ERDUNGSBOLZEN, RAUM_FELDER, RAUM_ANZAHL, isoliermattenSoll } from '../lib/auswahllisten';
import { raeumeAnzahl, schilderSoll, feuerloescherBefund, ekSoll } from '../lib/pruefpunktLogik';
import SchutzgeraetBlock, { SchutzpruefDateien, hatSchutzgeraet } from './SchutzgeraetBlock';
import { zeileSpeichern, offeneZeile } from '../lib/warteschlange';
import { FotoFeld } from './Fotos';
import { useAuth } from '../hooks/useAuth';
import { t } from '../lib/sprache';
import ZaehlerEditor from './ZaehlerEditor';

const MS_HERSTELLER_LISTE = MS_ANLAGEN.map(e => e.hersteller);

// Karte oeffnet mit dem Stand des Geraets: Wartendes ueber die Daten legen.
function mitWartendem(art, obj, schluesselFeld = 'id') {
  if (!obj) return obj;
  const offen = offeneZeile(art, obj[schluesselFeld]);
  if (!Object.keys(offen).length) return obj;
  const erg = { ...obj, ...offen };
  // Welche Felder noch warten, merkt sich useAutoSave: Die darf ein spaeteres
  // Nachladen (Server-Stand ohne sie) nicht zuruecksetzen. Nicht aufzaehlbar,
  // damit es nie mitgespeichert wird.
  Object.defineProperty(erg, '_wartendeFelder', { value: Object.keys(offen), enumerable: false });
  return erg;
}

// Nur die Felder, die sich wirklich geaendert haben (flacher Vergleich; der
// Editor erzeugt fuer geaenderte Felder neue Objekte).
function geaenderteFelder(alt, neu) {
  const p = {};
  for (const k of Object.keys(neu || {})) if (neu[k] !== (alt || {})[k]) p[k] = neu[k];
  return p;
}

// Neue Datensatz-ID schon im Geraet: Dann laesst sich ein neues MS-Feld auch
// ohne Netz anlegen und spaeter nachsenden.
function neueId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

// Muss zur Pruefregel in der Datenbank passen (Migration 28). Vorher stand hier
// ein freies Textfeld - ein Tippfehler haette die Aenderung stumm scheitern
// lassen, weil die Datenbank nur diese vier Werte annimmt.
const ISOLIERART = [
  // Reihenfolge und Texte: Anna-Lena 18.09.2026. Nur bei offener Bauweise passt
  // die Fachbroschuere 01.
  { value: 'luft_offen', label: 'Luft, offene Bauweise ohne Berührungsschutz' },
  { value: 'luft', label: 'Luft (gekapselt)' },
  { value: 'sf6', label: 'SF6-Gas' },
  { value: 'clean_air', label: 'Clean Air' },
];

// Generischer Auto-Save-Hook für ein einzelnes Objekt
// saveFn(next, aenderungen): next = kompletter Stand der Karte, aenderungen =
// NUR die seit dem letzten Speichern geaenderten Felder. Gespeichert werden
// seit dem 13.09.2026 nur noch die Aenderungen: Vorher schickte jede Karte ihre
// ganze Kopie der Zeile - offline veraltet - und setzte damit Eingaben aus
// einem anderen Kapitel zurueck (Bauform in 01, Hersteller in 02).
function useAutoSave(initial, saveFn, delay = 800) {
  const [data, setData] = useState(initial);
  const [status, setStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'wartet' | 'error'
  const [error, setError] = useState(null);
  const timer = useRef(null);
  // Noch nicht abgegeben: { next, aenderungen }. Wird bei einem Wechsel auf
  // einen ANDEREN Datensatz, beim Verlassen, beim Speichern-Knopf und beim
  // Wechsel in den Hintergrund SOFORT abgegeben.
  const offen = useRef(null);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;
  const dataRef = useRef(initial);
  // Felder, die hier angefasst wurden - die bleiben beim Nachladen stehen.
  const angefasst = useRef(new Set(initial?._wartendeFelder || []));

  // Nur bei ID-Wechsel neu initialisieren — initial-Objekt-Referenz absichtlich ignoriert
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(initial); dataRef.current = initial; angefasst.current = new Set(initial?._wartendeFelder || []); }, [initial?.id]);

  // Frischer Stand (Nachladen nach dem Speichern, Server nach Zwischenspeicher)
  // wird uebernommen - nur fuer Felder, die hier NICHT angefasst wurden.
  // Vorher zeigte die Karte bis zum Verlassen den allerersten Stand.
  useEffect(() => {
    const cur = dataRef.current;
    if (!initial || !cur || cur.id !== initial.id) return;
    (initial._wartendeFelder || []).forEach(k => angefasst.current.add(k));
    let neu = null;
    for (const [k, v] of Object.entries(initial)) {
      if (angefasst.current.has(k)) continue;
      if (JSON.stringify(cur[k]) !== JSON.stringify(v)) { neu = neu || { ...cur }; neu[k] = v; }
    }
    if (neu) { dataRef.current = neu; setData(neu); }
  }, [initial]);

  const ausfuehren = async (o) => {
    setStatus('saving');
    setError(null);
    try {
      const r = await saveFnRef.current(o.next, o.aenderungen);
      if (r && r.status === 'wartet') {
        // Kein Netz: steht sicher im Geraet und geht automatisch raus.
        setStatus('wartet');
      } else {
        setStatus('saved');
        setTimeout(() => setStatus(st => (st === 'saved' ? 'idle' : st)), 1500);
      }
    } catch (e) {
      setError(e.message || 'Speicher-Fehler');
      setStatus('error');
    }
  };

  const abgeben = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (offen.current) { const o = offen.current; offen.current = null; ausfuehren(o); }
  };

  // Speichern-Knopf / App geht in den Hintergrund: Offenes SOFORT abgeben.
  // Beim Verlassen ebenso.
  useEffect(() => {
    window.addEventListener('etb-jetzt-speichern', abgeben);
    return () => { window.removeEventListener('etb-jetzt-speichern', abgeben); abgeben(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (patch) => {
    if (!patch || Object.keys(patch).length === 0) return;
    Object.keys(patch).forEach(k => angefasst.current.add(k));
    const next = { ...(dataRef.current || data), ...patch };
    dataRef.current = next;
    setData(next);
    if (offen.current && offen.current.next?.id !== next.id) abgeben();
    offen.current = { next, aenderungen: { ...(offen.current?.aenderungen || {}), ...patch } };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(abgeben, delay);
  };

  return { data, update, status, error };
}

function StatusBadge({ status, error }) {
  if (status === 'saving') return <span style={{ fontSize: 13, color: C.gray500 }}>{t('speichert...')}</span>;
  if (status === 'wartet') return <span style={{ fontSize: 13, color: C.orangeText, fontWeight: 700 }}>{t('kein Netz – auf dem Gerät gespeichert')}</span>;
  if (status === 'saved')  return <span style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>✓ {t('gespeichert')}</span>;
  if (status === 'error')  return <span style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>! {error}</span>;
  return null;
}

// === Einzelner Trafo - editierbar ===========================================
export function EditableTrafoCard({ trafo, onSaved }) {
  const { data, update, status, error } = useAutoSave(mitWartendem('trafo', trafo), async (next, aenderungen) => {
    try {
      // Ueber die Warteschlange: ohne Netz im Geraet gemerkt, spaeter gesendet.
      // Nur die geaenderten Felder - der Trafo wird auch im Kapitel Oelwanne bearbeitet.
      const r = await zeileSpeichern('trafo', next.id, { id: next.id, ...aenderungen });
      if (r.status === 'gesendet' && onSaved) onSaved(next);
      return r;
    } catch (e) {
      // Kennt die Datenbank ein Feld nicht, lehnt sie den ganzen Trafo ab.
      // Die englische Originalmeldung versteht draussen niemand.
      const fehlt = String(e.message || '').match(/Could not find the '([^']+)' column/);
      if (fehlt) throw new Error('Nicht gespeichert: Die Datenbank kennt das Feld „' + fehlt[1]
        + '" noch nicht (Migration fehlt). Bitte im Büro Bescheid geben.');
      throw e;
    }
  });

  if (!data) return null;

  // Derselbe Editor wie in den Stammdaten, im Einzelmodus: alle Felder, auch
  // Schutzfunktionen, Anschluss-Status, NS-Kabel, Anschlüsse und Stufen-
  // spannungen. Vorher gab es hier eine abgespeckte Kopie mit einem Drittel
  // der Felder - Anna-Lena: "da fehlen einige bzw. alle zum Schutz, zum Kabel".
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', minHeight: 20, marginBottom: 4 }}>
        <StatusBadge status={status} error={error}/>
      </div>
      <TrafosEditor einzeln items={[data]} setItems={(liste) => update(geaenderteFelder(data, liste[0]))}/>
    </div>
  );
}

// === Auftraggeber (Kunde) - editierbar =====================================
export function EditableKundeCard({ kundeId, onSaved }) {
  const { kunde } = useKunde(kundeId);
  const { data, update, status, error } = useAutoSave(mitWartendem('kunde', kunde), async (next, aenderungen) => {
    if (!next?.id) return null;
    // Leere Kundennummer wird beim Senden zu NULL (siehe warteschlange.js):
    // '' ist ein Wert, und die Kundennummer ist eindeutig.
    const r = await zeileSpeichern('kunde', next.id, { id: next.id, ...aenderungen });
    if (r.status === 'gesendet' && onSaved) onSaved(next);
    return r;
  });

  if (!kunde || !data) {
    return <div style={{ padding: 12, color: C.gray500, fontStyle: 'italic', fontSize: 15 }}>{t('Kein Auftraggeber verknuepft.')}</div>;
  }

  return (
    <div style={{ background: C.gray100, borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid ' + C.gray200 }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('Auftraggeber: {name}', { name: data.name || t('(ohne Namen)') })}
        </div>
        <StatusBadge status={status} error={error}/>
      </div>
      <Row columns={2}>
        <Field label={t('Kundennummer')}><TextInput value={data.kunden_nr} onChange={v => update({ kunden_nr: v })}/></Field>
        <Field label={t('Name')}><TextInput value={data.name} onChange={v => update({ name: v })}/></Field>
      </Row>
      <Row columns={3}>
        <Field label={t('Strasse')}><TextInput value={data.strasse} onChange={v => update({ strasse: v })}/></Field>
        <Field label={t('PLZ')}><TextInput value={data.plz} onChange={v => update({ plz: v })}/></Field>
        <Field label={t('Ort')}><TextInput value={data.ort} onChange={v => update({ ort: v })}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Ansprechpartner')}><TextInput value={data.ansprechpartner} onChange={v => update({ ansprechpartner: v })}/></Field>
        <Field label={t('Position')}><TextInput value={data.position} onChange={v => update({ position: v })}/></Field>
      </Row>
      <Row columns={3}>
        <Field label={t('Telefon')}><TextInput value={data.telefon} onChange={v => update({ telefon: v })}/></Field>
        <Field label={t('Mobil')}><TextInput value={data.mobil} onChange={v => update({ mobil: v })}/></Field>
        <Field label={t('E-Mail')}><TextInput value={data.email} onChange={v => update({ email: v })} type='email'/></Field>
      </Row>
    </div>
  );
}

// === Stations-Stammdaten (Gebäude) - editierbar ===========================
export function EditableStationCard({ station, config, onSavedStation, onSavedConfig }) {
  const stam = useAutoSave(mitWartendem('station', station), async (next, aenderungen) => {
    const r = await zeileSpeichern('station', next.id, { id: next.id, ...aenderungen });
    if (r.status === 'gesendet' && onSavedStation) onSavedStation(next);
    return r;
  });
  const conf = useAutoSave(mitWartendem('konfig', config || { station_id: station?.id }, 'station_id'), async (next, aenderungen) => {
    if (!station?.id) return null;
    const r = await zeileSpeichern('konfig', station.id, { station_id: station.id, ...aenderungen });
    if (r.status === 'gesendet' && onSavedConfig) onSavedConfig(next);
    return r;
  });

  if (!stam.data) return null;

  return (
    <div style={{ background: C.gray100, borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid ' + C.gray200 }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('Stations-Stammdaten')}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <StatusBadge status={stam.status} error={stam.error}/>
          <StatusBadge status={conf.status} error={conf.error}/>
        </div>
      </div>

      <Row columns={2}>
        <Field label={t('Bezeichnung')} hint={t('Vorschlag antippen und frei ergänzen, z. B. „Unterstation Süd 1“')}>
          <><BezeichnungVorschlaege value={stam.data.bezeichnung} onChange={v => stam.update({ bezeichnung: v })}/>
          <TextInput value={stam.data.bezeichnung} onChange={v => stam.update({ bezeichnung: v })}/></>
        </Field>
        <Field label={t('Stations-Nr')}><TextInput value={stam.data.stations_nr} onChange={v => stam.update({ stations_nr: v, vnb_stationsbezeichnung: v })}/></Field>
        {/* Bauform - dasselbe Feld wie im Stammdaten-Formular und in der Planung
            (station_konfiguration.stations_typ, siehe Migration 36). */}
        <Field label={t('Bauform')}><Select value={conf.data?.stations_typ} onChange={v => conf.update({ stations_typ: v })} options={BAUFORM.map(o => ({ ...o, label: t(o.label) }))}/></Field>
      </Row>
      <Row columns={3}>
        <Field label={t('Strasse')}><TextInput value={stam.data.standort_strasse} onChange={v => stam.update({ standort_strasse: v })}/></Field>
        <Field label={t('PLZ')}><TextInput value={stam.data.standort_plz} onChange={v => stam.update({ standort_plz: v })}/></Field>
        <Field label={t('Ort')}><TextInput value={stam.data.standort_ort} onChange={v => stam.update({ standort_ort: v })}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Hersteller (Gebäude)')}><TextInput value={conf.data?.stations_hersteller} onChange={v => conf.update({ stations_hersteller: v })}/></Field>
        <Field label={t('Modelltyp')}><TextInput value={conf.data?.stations_modelltyp} onChange={v => conf.update({ stations_modelltyp: v })}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Seriennummer')}><TextInput value={conf.data?.stations_seriennummer} onChange={v => conf.update({ stations_seriennummer: v })}/></Field>
        <Field label={t('Baujahr')}><NumberInput value={conf.data?.stations_baujahr || stam.data.baujahr} onChange={v => conf.update({ stations_baujahr: v })}/></Field>
      </Row>
      {/* Raeume: erst die Bauform, bei einer Kompaktstation entfaellt die Frage.
          Daraus schlaegt die App die Stueckzahl Isoliermatten (05.7) vor. */}
      {conf.data?.stations_typ !== 'kompakt' && (
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: '6px 0 2px' }}>{t('Räume der Station')}</div>
      )}
      {conf.data?.stations_typ !== 'kompakt' && (
        <Row columns={4}>
          {RAUM_FELDER.map(([feld, label]) => (
            <Field key={feld} label={t(label)}>
              <Select value={conf.data?.[feld] != null ? String(conf.data[feld]) : ''} onChange={v => conf.update({ [feld]: v === null || v === '' ? null : Number(v) })} options={RAUM_ANZAHL}/>
            </Field>
          ))}
        </Row>
      )}
    </div>
  );
}

// === MS-Anlage + MS-Felder - editierbar ====================================
// Liste von Stammdaten-Zeilen (Wandler) im Protokoll bearbeiten.
// Gleiche Regeln wie bei den MS-Feldern weiter unten: 0,8 s Wartezeit statt
// Speichern bei jedem Buchstaben, alles ueber die Warteschlange (ohne Netz im
// Geraet gemerkt), Offenes wird beim Speichern-Knopf und beim Verlassen sofort
// abgegeben, und Entfernen legt nur still (active = false).
function useZeilenListe({ art, items, stationId, onChanged }) {
  const [liste, setListe] = useState(items || []);
  const timer = useRef({});
  const [fehler, setFehler] = useState(null);
  const [wartet, setWartet] = useState(false);

  useEffect(() => {
    setListe((items || []).map(z => {
      const m = mitWartendem(art, z);
      const tippt = timer.current[z.id];
      return tippt ? { ...m, ...tippt.aenderungen } : m;
    }).filter(z => z.active !== false));
  }, [items, art]);

  const update = (idx, key, value) => {
    const neu = liste.map((z, i) => (i === idx ? { ...z, [key]: value } : z));
    setListe(neu);
    const schluessel = neu[idx]?.id || ('neu-' + idx);
    const vorher = timer.current[schluessel];
    if (vorher) clearTimeout(vorher.t);
    const aenderungen = { ...(vorher?.aenderungen || {}), [key]: value };
    const run = async () => {
      delete timer.current[schluessel];
      const id = neu[idx]?.id;
      if (!id) return;
      try {
        const r = await zeileSpeichern(art, id, { id, station_id: stationId, position: idx, ...aenderungen });
        setFehler(null);
        setWartet(r.status === 'wartet');
        if (r.status === 'gesendet' && onChanged) onChanged();
      } catch (e) {
        setFehler(t('Zeile {nr} wurde von der Datenbank abgelehnt: {fehler}. Bitte im Büro Bescheid geben.',
          { nr: idx + 1, fehler: e.message || t('unbekannter Fehler') }));
      }
    };
    timer.current[schluessel] = { t: setTimeout(run, 800), run, aenderungen };
  };

  useEffect(() => {
    const sofort = () => Object.values(timer.current).forEach(({ t: tm, run }) => { clearTimeout(tm); run(); });
    window.addEventListener('etb-jetzt-speichern', sofort);
    return () => window.removeEventListener('etb-jetzt-speichern', sofort);
  }, []);
  useEffect(() => {
    const offen = timer.current;
    return () => Object.values(offen).forEach(({ t: tm, run }) => { clearTimeout(tm); run(); });
  }, []);

  const legtAn = useRef(false);
  const hinzufuegen = async (vorgabe = {}) => {
    if (!stationId || legtAn.current) return;    // Doppeltipp legte zwei Zeilen an
    legtAn.current = true;
    setTimeout(() => { legtAn.current = false; }, 800);
    const neu = { id: neueId(), station_id: stationId, position: liste.length, active: true, ...vorgabe };
    setListe([...liste, neu]);
    try {
      const r = await zeileSpeichern(art, neu.id, neu);
      setWartet(r.status === 'wartet');
      if (r.status === 'gesendet' && onChanged) onChanged();
    } catch (e) {
      window.alert(t('Zeile konnte nicht angelegt werden: {fehler}', { fehler: e.message }));
    }
  };

  const entfernen = async (idx, frage) => {
    const z = liste[idx];
    if (!z?.id) { setListe(liste.filter((_, i) => i !== idx)); return; }
    if (!window.confirm(frage)) return;
    setListe(liste.filter((_, i) => i !== idx));
    try {
      const r = await zeileSpeichern(art, z.id, { id: z.id, active: false });
      if (r.status === 'gesendet' && onChanged) onChanged();
    } catch (e) {
      window.alert(t('Zeile konnte nicht entfernt werden: {fehler}', { fehler: e.message }));
    }
  };

  return { liste, update, hinzufuegen, entfernen, fehler, wartet };
}

// Strom- und Spannungswandler im Protokoll. Vorher standen sie NUR in den
// Stationsstammdaten im Büro - der Monteur konnte ein Typenschild vor Ort nicht
// nachtragen (Anna-Lena, 16.09.2026).
// Name eines Wandlers fuer Foto-Beschriftung und PDF ("Stromwandler 1").
function wandlerName(w, idx) {
  return ((w.wandler_typ || 'strom') === 'strom' ? 'Stromwandler ' : 'Spannungswandler ') + (idx + 1);
}
function WandlerBlock({ wandler, stationId, protokollId, onChanged, conf }) {
  const { user } = useAuth();
  const { liste, update, hinzufuegen, entfernen, fehler, wartet } =
    useZeilenListe({ art: 'wandler', items: wandler, stationId, onChanged });

  // NS-Messung: nur Stromwandler (Anna-Lena 18.09.2026).
  const nurStrom = conf?.data?.verrechnungsmessung === 'ns';

  const karte = (w, idx) => {
    const istStrom = (w.wandler_typ || 'strom') === 'strom';
    return (
      <div key={w.id || idx} style={{ background: C.white, borderRadius: 9, padding: 12,
                                      marginBottom: 10, border: '1px solid ' + C.gray200 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>
            {istStrom ? t('Stromwandler {nr}', { nr: idx + 1 }) : t('Spannungswandler {nr}', { nr: idx + 1 })}
          </div>
          <button type='button' onClick={() => entfernen(idx, t('Wandler {nr} wirklich entfernen?', { nr: idx + 1 }))}
            style={{ minHeight: 40, padding: '7px 13px', borderRadius: 7,
                     border: '1.5px solid ' + C.gray200, background: C.white,
                     color: C.gray700, fontSize: 14, fontWeight: 600,
                     cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('entfernen')}
          </button>
        </div>
        <Row columns={2}>
          <Field label={t('Art')}>
            <Select value={w.wandler_typ || 'strom'} onChange={v => update(idx, 'wandler_typ', v)}
              options={nurStrom && w.wandler_typ !== 'spannung' ? [{ value: 'strom', label: t('Stromwandler') }] : [{ value: 'strom', label: t('Stromwandler') }, { value: 'spannung', label: t('Spannungswandler') }]}/>
          </Field>
          <Field label={t('Hersteller')}><TextInput value={w.hersteller} onChange={v => update(idx, 'hersteller', v)}/></Field>
        </Row>
        <Row columns={2}>
          <Field label={t('Typ / Modell')}><TextInput value={w.typ} onChange={v => update(idx, 'typ', v)}/></Field>
          {istStrom
            ? <Field label={t('Bemessungsübersetzung')} hint={t('z. B. 200/5 A')}><TextInput value={w.bemessunguebersetzung} onChange={v => update(idx, 'bemessunguebersetzung', v)}/></Field>
            : <Field label={t('Spannung e-n')} hint={t('z. B. 100/√3 V')}><TextInput value={w.spannung_e_n} onChange={v => update(idx, 'spannung_e_n', v)}/></Field>}
        </Row>
        <Row columns={3}>
          {!istStrom && <Field label={t('Spannung U-X')}><TextInput value={w.spannung_u_x} onChange={v => update(idx, 'spannung_u_x', v)}/></Field>}
          <Field label={t('Genauigkeitsklasse')} hint={t('z. B. 0,5s')}><TextInput value={w.genauigkeitsklasse} onChange={v => update(idx, 'genauigkeitsklasse', v)}/></Field>
          <Field label={t('Bemessungsbürde [VA]')}><NumberInput value={w.bemessungsbuerde_va} onChange={v => update(idx, 'bemessungsbuerde_va', v)}/></Field>
          <Field label={t('Eichjahr')}><NumberInput value={w.eichjahr} onChange={v => update(idx, 'eichjahr', v)}/></Field>
        </Row>
        <Row columns={3}>
          <Field label={t('Seriennummer L1')}><TextInput value={w.seriennummer_l1} onChange={v => update(idx, 'seriennummer_l1', v)}/></Field>
          <Field label={t('Seriennummer L2')}><TextInput value={w.seriennummer_l2} onChange={v => update(idx, 'seriennummer_l2', v)}/></Field>
          <Field label={t('Seriennummer L3')}><TextInput value={w.seriennummer_l3} onChange={v => update(idx, 'seriennummer_l3', v)}/></Field>
        </Row>
        <Field label={t('Bemerkung')}><TextInput value={w.notizen} onChange={v => update(idx, 'notizen', v)}/></Field>
        {/* Fotos je Wandler (Anna-Lena 24.09.2026) - erst moeglich, wenn die
            Zeile eine ID hat (sonst gaebe es keine Kategorie zum Zuordnen). */}
        {w.id && protokollId && (
          <FotoFeld label={'📷 ' + t('Fotos {name}', { name: wandlerName(w, idx) })}
            kategorie={'wandler:' + w.id} titel={wandlerName(w, idx)}
            stationId={stationId} protokollId={protokollId} userId={user?.id}/>
        )}
      </div>
    );
  };

  return (
    <Subsection title={t('Wandler ({n})', { n: liste.length })}>
      {wartet && !fehler && (
        <div style={{ padding: '8px 12px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow,
                      borderRadius: 6, marginBottom: 10, fontSize: 14, color: C.navy }}>
          {t('Kein Netz: Die Wandler sind auf dem Gerät gespeichert und werden automatisch gesendet.')}
        </div>
      )}
      {fehler && (
        <div style={{ padding: '10px 12px', background: C.redBg, border: '2px solid ' + C.red,
                      borderRadius: 8, marginBottom: 10, fontSize: 15, color: C.red, fontWeight: 700, lineHeight: 1.4 }}>
          {fehler}
        </div>
      )}
      {liste.length === 0 && (
        <div style={{ fontSize: 15, color: C.gray500, fontStyle: 'italic', marginBottom: 10 }}>
          {t('Noch keine Wandler erfasst. Unten hinzufügen.')}
        </div>
      )}
      {liste.map(karte)}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['strom', t('+ Stromwandler'), 'stromwandler_vorhanden'], ['spannung', t('+ Spannungswandler'), 'spannungswandler_vorhanden']].filter(([typ]) => !(nurStrom && typ === 'spannung')).map(([typ, text, merker]) => (
          <button key={typ} type='button'
            onClick={() => { hinzufuegen({ wandler_typ: typ }); if (conf && conf.data?.[merker] !== true) conf.update({ [merker]: true }); }}
            style={{ flex: '1 1 200px', minHeight: 48, padding: '11px 16px', borderRadius: 9,
                     border: '2px dashed ' + C.gray300, background: C.white, color: C.navy,
                     fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
            {text}
          </button>
        ))}
      </div>
    </Subsection>
  );
}

export function EditableMsAnlage({ config, msFelder, stationId, protokollId, onChanged }) {
  const { user: nutzer } = useAuth();
  const conf = useAutoSave(mitWartendem('konfig', config || { station_id: stationId }, 'station_id'), async (next, aenderungen) => {
    if (!stationId) return null;
    const r = await zeileSpeichern('konfig', stationId, { station_id: stationId, ...aenderungen });
    if (r.status === 'gesendet' && onChanged) onChanged();
    return r;
  });

  const [felder, setFelder] = useState(msFelder || []);
  const feldTimer = useRef({});
  useEffect(() => {
    // Was gerade getippt wird (noch in der 0,8-s-Wartezeit), bleibt stehen -
    // vorher sprang es nach jedem Nachladen kurz auf den alten Wert zurueck.
    setFelder((msFelder || []).map(f => {
      const m = mitWartendem('msfeld', f);
      const tippt = feldTimer.current[f.id];
      return tippt ? { ...m, ...tippt.aenderungen } : m;
    }).filter(f => f.active !== false));
  }, [msFelder]);

  // Beim Tippen NICHT bei jedem Buchstaben speichern.
  //
  // Vorher ging bei jedem Tastendruck ein Schreibvorgang an die Datenbank —
  // eine zwölfstellige Seriennummer waren zwölf Anfragen, und danach jeweils
  // ein Nachladen der Station. Am Telefon mit schwachem Empfang staut sich das
  // auf. Und ein Fehler wurde gar nicht geprüft: Ging das Speichern schief,
  // stand die Nummer weiter auf dem Bildschirm, aber nirgends sonst.
  const [feldFehler, setFeldFehler] = useState(null);
  const [feldWartet, setFeldWartet] = useState(false);

  const updateFeld = (idx, key, value) => {
    const list = felder.map((f, i) => (i === idx ? { ...f, [key]: value } : f));
    setFelder(list);

    const schluessel = list[idx]?.id || ('neu-' + idx);
    const vorher = feldTimer.current[schluessel];
    if (vorher) clearTimeout(vorher.t);
    // Nur die geaenderten Felder dieses MS-Felds sammeln und senden.
    const aenderungen = { ...(vorher?.aenderungen || {}), [key]: value };
    const run = async () => {
      delete feldTimer.current[schluessel];
      const id = list[idx]?.id;
      if (!id) return;
      try {
        // Ueber die Warteschlange: ohne Netz im Geraet gemerkt, spaeter gesendet.
        const r = await zeileSpeichern('msfeld', id, { id, station_id: stationId, position: idx, ...aenderungen });
        setFeldFehler(null);
        setFeldWartet(r.status === 'wartet');
        if (r.status === 'gesendet' && onChanged) onChanged();
      } catch (e) {
        setFeldFehler(t('Feld {nr} wurde von der Datenbank abgelehnt: {fehler}. Bitte im Büro Bescheid geben.',
          { nr: idx + 1, fehler: e.message || t('unbekannter Fehler') }));
      }
    };
    feldTimer.current[schluessel] = { t: setTimeout(run, 800), run, aenderungen };
  };

  // Speichern-Knopf / App in den Hintergrund: Offenes SOFORT abgeben.
  useEffect(() => {
    const sofort = () => {
      Object.values(feldTimer.current).forEach(({ t, run }) => { clearTimeout(t); run(); });
    };
    window.addEventListener('etb-jetzt-speichern', sofort);
    return () => window.removeEventListener('etb-jetzt-speichern', sofort);
  }, []);

  // Beim Verlassen (Reiter wechseln, "Weiter"): Offenes SOFORT speichern.
  // Vorher wurden die Zeitgeber hier nur abgeraeumt - eine Seriennummer,
  // die jemand in den letzten 0,8 s vor "Weiter" getippt hatte, war weg.
  useEffect(() => {
    const timer = feldTimer.current;
    return () => {
      Object.values(timer).forEach(({ t, run }) => { clearTimeout(t); run(); });
    };
  }, []);

  // Sind vor Ort mehr Felder da als im Büro erfasst, muss der Monteur eines
  // ergänzen können — sonst fehlt es im ganzen Protokoll und im PDF.
  // Die ID entsteht im Geraet - so klappt das Anlegen auch ohne Netz.
  const legtAn = useRef(false);
  const feldHinzufuegen = async () => {
    if (!stationId || legtAn.current) return;   // Doppeltipp legte zwei Felder an
    legtAn.current = true;
    setTimeout(() => { legtAn.current = false; }, 800);
    const neu = { id: neueId(), station_id: stationId, position: felder.length, bezeichnung: '', active: true };
    setFelder([...felder, neu]);
    try {
      const r = await zeileSpeichern('msfeld', neu.id, neu);
      setFeldWartet(r.status === 'wartet');
      if (r.status === 'gesendet' && onChanged) onChanged();
    } catch (e) {
      window.alert(t('Feld konnte nicht angelegt werden: {fehler}', { fehler: e.message }));
    }
  };

  // Nicht löschen, sondern stilllegen: an einem Feld können Messwerte hängen.
  const feldEntfernen = async (idx) => {
    const f = felder[idx];
    if (!f?.id) { setFelder(felder.filter((_, i) => i !== idx)); return; }
    if (!window.confirm(t('Feld {nr} wirklich entfernen?', { nr: idx + 1 }))) return;
    setFelder(felder.filter((_, i) => i !== idx));
    try {
      const r = await zeileSpeichern('msfeld', f.id, { id: f.id, active: false });
      if (r.status === 'gesendet' && onChanged) onChanged();
    } catch (e) {
      window.alert(t('Feld konnte nicht entfernt werden: {fehler}', { fehler: e.message }));
    }
  };

  return (
    <div style={{ background: C.gray100, borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('MS-Anlage')}
      </div>
      <Row columns={2}>
        <Field label={t('Hersteller')}><ComboInput value={conf.data?.ms_anlage_hersteller} onChange={v => conf.update({ ms_anlage_hersteller: v })} options={MS_HERSTELLER_LISTE} placeholder={t('z. B. Siemens')}/></Field>
        <Field label={t('Anzahl Felder')}><NumberInput value={conf.data?.ms_anlage_anzahl_felder} onChange={v => conf.update({ ms_anlage_anzahl_felder: v })}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Betriebsspannung [kV]')}><NumberInput value={conf.data?.ms_anlage_betriebsspannung_kv} onChange={v => conf.update({ ms_anlage_betriebsspannung_kv: v })}/></Field>
        <Field label={t('Isolierart')}><Select value={conf.data?.ms_anlage_isolierart} onChange={v => conf.update({ ms_anlage_isolierart: v })} options={ISOLIERART.map(o => ({ ...o, label: t(o.label) }))}/></Field>
      </Row>
      {/* Nur Luftanlagen: ohne Erdungsschalter braucht jeder Abgang eine E+K (05.10). */}
      {(conf.data?.ms_anlage_isolierart === 'luft' || conf.data?.ms_anlage_isolierart === 'luft_offen') && (
        <Row columns={2}>
          <Field label={t('Erdungsschalter in den Feldern vorhanden')}>
            <Select value={conf.data?.ms_erdungsschalter === true ? 'ja' : conf.data?.ms_erdungsschalter === false ? 'nein' : ''}
              onChange={v => conf.update({ ms_erdungsschalter: v === 'ja' ? true : v === 'nein' ? false : null })}
              options={[{ value: 'ja', label: 'ja' }, { value: 'nein', label: 'nein' }].map(o => ({ ...o, label: t(o.label) }))}/>
          </Field>
        </Row>
      )}

      {/* Alle Typenschilddaten je Feld. Das Büro kennt beim Anlegen meist nur
          die Anzahl der Felder — Seriennummer, Typ und Baujahr nimmt der Monteur
          vor Ort auf. Anna-Lena: „daher müssen alle Stammdaten bei der Wartung
          bearbeitet werden können". */}
      <Subsection title={t('MS-Felder ({n})', { n: felder.length })}>
        {/* Isolierart aus dem Anlagentyp ableiten (z. B. 8DJH -> SF6). Nur als
            Vorschlag, und nur wenn sie in den Stammdaten noch fehlt - sie
            steuert die Zubehoer-Vorschlaege. */}
        {(() => {
          if (conf.data?.ms_anlage_isolierart) return null;
          const feld = felder.find(f => f.typ);
          const iso = feld ? isolierartFuerMsTyp(feld.hersteller || conf.data?.ms_anlage_hersteller, feld.typ) : null;
          const erlaubt = ['luft', 'luft_offen', 'sf6', 'clean_air'];
          if (!iso || !erlaubt.includes(iso)) return null;
          const text = ISOLIERART.find(o => o.value === iso)?.label || iso;
          return (
            <div style={{ padding: '8px 12px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6,
                          marginBottom: 10, fontSize: 14, color: C.navy, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{t('Typ „{typ}" ist üblicherweise', { typ: feld.typ })} <strong>{t(text)}</strong>.</span>
              <button type='button' onClick={() => conf.update({ ms_anlage_isolierart: iso })}
                style={{ minHeight: 38, padding: '5px 12px', borderRadius: 7, border: '1.5px solid ' + C.navy, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>
                {t('Isolierart übernehmen')}
              </button>
            </div>
          );
        })()}
        {feldWartet && !feldFehler && (
          <div style={{ padding: '8px 12px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow,
                        borderRadius: 6, marginBottom: 10, fontSize: 14, color: C.navy }}>
            {t('Kein Netz: Die MS-Felder sind auf dem Gerät gespeichert und werden automatisch gesendet.')}
          </div>
        )}
        {feldFehler && (
          <div style={{ padding: '10px 12px', background: C.redBg, border: '2px solid ' + C.red,
                        borderRadius: 8, marginBottom: 10, fontSize: 15, color: C.red,
                        fontWeight: 700, lineHeight: 1.4 }}>
            {feldFehler}
          </div>
        )}
        {felder.length === 0 && (
          <div style={{ fontSize: 15, color: C.gray500, fontStyle: 'italic', marginBottom: 10 }}>
            {t('Noch keine Felder erfasst. Unten hinzufügen.')}
          </div>
        )}
        {felder.map((f, idx) => (
          <div key={f.id || idx} style={{ background: C.white, borderRadius: 9, padding: 12,
                                          marginBottom: 10, border: '1px solid ' + C.gray200 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 800, color: C.navy, fontSize: 15 }}>{t('Feld {nr}', { nr: idx + 1 })}</div>
              <button type='button' onClick={() => feldEntfernen(idx)}
                style={{ minHeight: 40, padding: '7px 13px', borderRadius: 7,
                         border: '1.5px solid ' + C.gray200, background: C.white,
                         color: C.gray700, fontSize: 14, fontWeight: 600,
                         cursor: 'pointer', fontFamily: 'inherit' }}>
                {t('entfernen')}
              </button>
            </div>
            <Row columns={2}>
              <Field label={t('Feld-Nr.')} hint={t('z. B. J01')}><TextInput value={f.feld_nr} onChange={v => updateFeld(idx, 'feld_nr', v)}/></Field>
              <Field label={t('Typ')}><Select value={f.typ_code} onChange={v => updateFeld(idx, 'typ_code', v)} options={MS_FELD_TYPEN.map(o => ({ ...o, label: t(o.label) }))}/></Field>
            </Row>
            <Row columns={2}>
              <Field label={t('Bezeichnung')} hint={t('z. B. Ringkabel Nord')}><TextInput value={f.bezeichnung} onChange={v => updateFeld(idx, 'bezeichnung', v)}/></Field>
              <Field label={t('Hersteller')}><ComboInput value={f.hersteller} onChange={v => updateFeld(idx, 'hersteller', v)} options={MS_HERSTELLER_LISTE} placeholder={t('z. B. Siemens')}/></Field>
            </Row>
            <Row columns={3}>
              <Field label={t('Typ / Modell')} hint={typenFuerMsHersteller(f.hersteller || conf.data?.ms_anlage_hersteller).length ? t('Baureihen des Herstellers zur Auswahl') : undefined}>
                <ComboInput value={f.typ} onChange={v => updateFeld(idx, 'typ', v)} options={typenFuerMsHersteller(f.hersteller || conf.data?.ms_anlage_hersteller)} placeholder={t('z. B. 8DJH')}/>
              </Field>
              <Field label={t('Seriennummer')}><TextInput value={f.seriennummer} onChange={v => updateFeld(idx, 'seriennummer', v)}/></Field>
              <Field label={t('Herstellungsjahr')}><NumberInput value={f.herstellungsjahr} onChange={v => updateFeld(idx, 'herstellungsjahr', v)}/></Field>
            </Row>
            <Field label={t('Bemerkung')}><TextInput value={f.notizen} onChange={v => updateFeld(idx, 'notizen', v)}/></Field>
            {/* Leistungsschalterfeld: UMZ-/Schutzgeraet ablesen (Migration 58). Pruef-
                protokolle laedt das Buero hoch - hier nur ansehen. */}
            {hatSchutzgeraet(f) && (
              <>
                <SchutzgeraetBlock wert={f.schutzgeraet} onChange={v => updateFeld(idx, 'schutzgeraet', v)}/>
                {f.id && <SchutzpruefDateien stationId={stationId} feldId={f.id} darfHochladen={false}/>}
                {/* Fotos vom UMZ-/Schutzgeraet (Anna-Lena 24.09.2026) */}
                {f.id && protokollId && (
                  <FotoFeld label={'📷 ' + t('Fotos UMZ-/Schutzgerät {feld}', { feld: f.feld_nr || idx + 1 })}
                    kategorie={'umz:' + f.id} titel={'UMZ-/Schutzgerät Feld ' + (f.feld_nr || idx + 1)}
                    stationId={stationId} protokollId={protokollId} userId={nutzer?.id}/>
                )}
              </>
            )}
          </div>
        ))}
        <button type='button' onClick={feldHinzufuegen}
          style={{ width: '100%', minHeight: 48, padding: '11px 16px', borderRadius: 9,
                   border: '2px dashed ' + C.gray300, background: C.white, color: C.navy,
                   fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('+ Feld hinzufügen')}
        </button>
      </Subsection>

      {/* Wandler stehen seit 08.10.2026 im eigenen Kapitel "Wandler / Zähler"
          (Anna-Lena: "Wandler als separaten Punkt, nicht unter MS-Anlage"). */}
    </div>
  );
}

// === Wandler / Zaehler - eigenes Kapitel im Protokoll (08.10.2026) ==========
export const WANDLER_ZAEHLER_CODE = 'WANDLER-ZAEHLER';
// Kein Kapitel aus der Datenbank: ProtokollFormPage setzt es hinter die
// MS-Anlage. Es hat keine Pruefpunkte, nur Stammdaten (Wandler + Zaehler).
export function EditableWandlerZaehler({ config, wandler, stationId, protokollId, onChanged }) {
  const conf = useAutoSave(mitWartendem('konfig', config || { station_id: stationId }, 'station_id'), async (next, aenderungen) => {
    if (!stationId) return null;
    const r = await zeileSpeichern('konfig', stationId, { station_id: stationId, ...aenderungen });
    if (r.status === 'gesendet' && onChanged) onChanged();
    return r;
  });
  return (
    <div>
      <WandlerBlock wandler={wandler} stationId={stationId} protokollId={protokollId} onChanged={onChanged} conf={conf}/>
      <Subsection title={t('Zähler ({n})', { n: Array.isArray(conf.data?.zaehler) ? conf.data.zaehler.length : 0 })}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6, marginBottom: 6 }}>
          <StatusBadge status={conf.status} error={conf.error}/>
        </div>
        <ZaehlerEditor items={conf.data?.zaehler} onChange={v => conf.update({ zaehler: v })} wandler={wandler} kompakt/>
      </Subsection>
    </div>
  );
}

// === Trafo + Ölwannen-Block kombiniert (für Wartungs-Modul 05) ==============
// Gleicher useAutoSave-Mechanismus wie EditableTrafoCard, aber:
// 1. Zeigt zusätzlich den OelWanneBlock mit Volumen-Berechnung
// 2. Wirft beim allerersten Klick einen Bestätigungs-Dialog
//    ("Stammdaten überschreiben?")
// 3. Schreibt direkt in die `station_trafos`-Tabelle, also überschreibt Stammdaten
function EditableTrafoMitWanne({ trafo, lastUpdated, onSaved }) {
  const { data, update, status, error } = useAutoSave(mitWartendem('trafo', trafo), async (next, aenderungen) => {
    const r = await zeileSpeichern('trafo', next.id, { id: next.id, ...aenderungen });
    if (r.status === 'gesendet' && onSaved) onSaved(next);
    return r;
  });

  const [confirmed, setConfirmed] = useState(false);

  if (!data) return null;
  const isOel = data.trafoart && data.trafoart !== 'giessharz';

  // Bestätigungs-Dialog beim ersten Mal
  const updateWithConfirm = (patch) => {
    if (!confirmed) {
      const datumText = lastUpdated ? '\n\n' + t('Letzte Aktualisierung der Stammdaten: {datum}', { datum: lastUpdated }) : '';
      const ok = window.confirm(
        t('Du änderst Stammdaten in der Wartung. Diese Werte werden direkt in den Stammdaten der Station gespeichert und überschreiben den vorherigen Stand.')
        + datumText + '\n\n' + t('Fortfahren?')
      );
      if (!ok) return;
      setConfirmed(true);
    }
    update(patch);
  };

  return (
    <div style={{ background: C.gray100, borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid ' + C.gray200 }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('Trafo: {name}', { name: data.bezeichnung || t('Trafo') })}
        </div>
        <StatusBadge status={status} error={error}/>
      </div>

      {/* Bewertungsrelevante Trafo-Werte (für die Ölwannen-Berechnung) */}
      <Row columns={2}>
        <Field label={t('Trafo-Art')}>
          <Select value={data.trafoart} onChange={v => updateWithConfirm({ trafoart: v })} options={[
            { value: 'hermetic', label: t('Hermetic (Öl, geschlossen)') },
            { value: 'giessharz', label: t('Giessharz (trocken)') },
            { value: 'oel_mit_ausdehner', label: t('Öl mit Ausdehner') },
          ]}/>
        </Field>
        {isOel && (
          <Field label={t('Gesamt-Ölgewicht [kg]')}>
            <NumberInput
              value={data.gesamt_oel_gewicht_kg ?? data.oel_gewicht_kg}
              onChange={v => updateWithConfirm({ gesamt_oel_gewicht_kg: v, oel_gewicht_kg: v })}/>
          </Field>
        )}
      </Row>

      {/* Eingebetteter Ölwannen-Block: Maße, Volumen-Berechnung, SVG, Bewertung */}
      <OelWanneBlock trafo={data} onChange={(key, value) => updateWithConfirm({ [key]: value })} />
    </div>
  );
}

// Automatische Bewertung (05.1.5 Oelwanne, 06.3 Erdung): Was die Automatik
// zuletzt als Befund gesehen hat, je Pruefpunkt. Bewusst AUSSERHALB der
// Komponente: Die Komponente wird bei jedem Reiterwechsel neu aufgebaut. Vorher
// vergass sie dabei alles und setzte eine Handbewertung des Monteurs (z. B.
// n.i.O. wegen korrodierter Klemme trotz guter Messwerte) wieder zurueck.
// Regel jetzt: Die Automatik schreibt nur, wenn der Punkt noch unbeantwortet
// ist oder sich der Befund seit ihrem letzten Blick geaendert hat.
const autoBefund = new Map();
function autoDarfSchreiben(pp, ziel) {
  const vorher = autoBefund.get(pp.id);
  autoBefund.set(pp.id, ziel);
  if (vorher === undefined) return pp.wert_auswahl == null && pp.in_ordnung == null;
  return vorher !== ziel;
}

// === NSHV "vorhanden / nicht vorhanden" (03) in die Stammdaten ==============
// Damit richten sich auch andere Kapitel danach (05 NH-Aufsteckgriff nur mit
// NSHV, Migration 55). Geschrieben wird nur, wenn es sich wirklich unterscheidet.
function NshvKonfigSync({ station, config, modul, onStationReload, aktiv }) {
  const antwort = (modul?.pruefpunkte || []).find(x => String(x.pos_nummer || '').trim() === '03')?.wert_auswahl;
  useEffect(() => {
    // Nur im offenen Protokoll: ein altes Protokoll darf die aktuellen Stammdaten
    // nicht ueberschreiben.
    if (!aktiv || !station?.id || !antwort) return;
    const soll = antwort === 'vorhanden' ? true : antwort === 'nicht vorhanden' ? false : null;
    if (soll === null || (config && config.nshv_vorhanden === soll)) return;
    zeileSpeichern('konfig', station.id, { station_id: station.id, nshv_vorhanden: soll })
      .then(r => { if (r?.status === 'gesendet' && onStationReload) onStationReload(); })
      .catch(() => { /* bleibt in der Warteschlange bzw. wird beim naechsten Mal erneut versucht */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [antwort, station?.id, config?.nshv_vorhanden, aktiv]);
  return null;
}

// === Isoliermatten (05.7): Stueck soll aus den Raeumen =======================
// Je MS-, NS- und kombiniertem MS/NS-Raum eine Matte (Anna-Lena 18.09.2026).
// Nur vorschlagen, wenn "Stück soll" noch leer ist - nie ueberschreiben.
// === Sicherheitszubehoer: Soll-Stueckzahlen und Auto-Bewertungen ============
// Anna-Lena 18.09.2026 (Migration 55/56):
//   05.7  Isoliermatten       je MS-, NS-, kombiniertem Raum 1
//   05.9  Warn-/Hinweisschilder schilderSoll(): je Raum 4, Rueckspannung ab 2
//         Ringkabelfeldern je Ringkabelfeld, je MS-Feld 2 ("nicht schalten",
//         "geerdet und kurzgeschlossen"); ist >= soll -> i.O., sonst n.i.O.
//   05.27 Handscheinwerfer    je Raum 1
//   05.30 Feuerloescher       je Raum 1
//   05.31 Pruefintervall      aus 05.30 "Letzte Pruefung": ueber 24 Monate -> n.i.O.
// "Stück soll" wird nur in ein LEERES Feld geschrieben und je Punkt nur einmal
// (Loeschen bleibt geloescht). Bewertet wird, wenn sich ist bzw. das Datum aendert,
// oder beim ersten Mal, solange der Punkt unbeantwortet ist. Nur im offenen Protokoll.
const sollVorgeschlagen = new Set();
const letzterWert = new Map();
function geaendert(schluessel, wert) {
  const hatte = letzterWert.has(schluessel);
  const vorher = letzterWert.get(schluessel);
  letzterWert.set(schluessel, wert);
  return { erstes: !hatte, anders: hatte && vorher !== wert };
}
const posVon = (pp) => String(pp?.pos_nummer || '').trim();

function ZubehoerSync({ config, msFelder, trafos, modul, onPruefpunktChange, aktiv }) {
  const matten = isoliermattenSoll(config);
  const ek = ekSoll(config, msFelder, trafos);
  const raeume = raeumeAnzahl(config);
  const schilder = schilderSoll(config, msFelder);
  useEffect(() => {
    if (!aktiv || !modul?.pruefpunkte || !onPruefpunktChange) return;
    const punkte = modul.pruefpunkte;
    const finde = (pos) => punkte.find(x => posVon(x) === pos);

    // 1) Soll-Stueckzahlen vorschlagen
    const sollJePos = { '05.7': matten, '05.9': schilder?.summe ?? null, '05.27': raeume, '05.30': raeume,
      '05.10': ek?.summe ?? null, '05.12': ek?.summe ?? null };
    for (const [pos, soll] of Object.entries(sollJePos)) {
      const pp = finde(pos);
      if (soll == null || !pp || sollVorgeschlagen.has(pp.id)) continue;
      sollVorgeschlagen.add(pp.id);
      const werte = pp.zusatz_werte || {};
      if (werte.stk_soll != null && werte.stk_soll !== '') continue;
      onPruefpunktChange(pp, { zusatz_werte: { ...werte, stk_soll: soll } });
    }

    // 2) Schilder: ist >= soll -> i.O., sonst n.i.O.
    const schild = finde('05.9');
    if (schild) {
      const w = schild.zusatz_werte || {};
      const soll = w.stk_soll === '' || w.stk_soll == null ? null : Number(w.stk_soll);
      const ist = w.stk_ist === '' || w.stk_ist == null ? null : Number(w.stk_ist);
      const { erstes, anders } = geaendert(schild.id + ':schilder', ist + '/' + soll);
      if (soll != null && ist != null && isFinite(soll) && isFinite(ist)
          && (anders || (erstes && schild.wert_auswahl == null))) {
        const ok = ist >= soll;
        const autoText = String(schild.bemerkung || '').startsWith('Auto-Bewertung:');
        if (ok && schild.in_ordnung !== true) {
          onPruefpunktChange(schild, { wert_auswahl: 'i.O.', in_ordnung: true, wert_modifiers: [], ...(autoText ? { bemerkung: null } : {}) });
        } else if (!ok && schild.in_ordnung !== false) {
          onPruefpunktChange(schild, { wert_auswahl: 'n.i.O.', in_ordnung: false,
            bemerkung: schild.bemerkung && !autoText ? schild.bemerkung : 'Auto-Bewertung: ' + ist + ' von ' + soll + ' Schildern vorhanden.' });
        }
      }
    }

    // 3) Feuerloescher-Pruefung (05.31) aus dem Datum bei 05.30
    const loescher = finde('05.30');
    const intervall = finde('05.31');
    if (loescher && intervall) {
      const datum = (loescher.zusatz_werte || {}).letzte_pruefung || null;
      const befund = datum ? feuerloescherBefund(datum) : null;
      const { erstes, anders } = geaendert(intervall.id + ':loescher', datum);
      if (befund && (anders || (erstes && intervall.wert_auswahl == null))) {
        const text = 'Auto-Bewertung: letzte Prüfung vor ' + befund.monate + ' Monaten'
          + (befund.naechste ? ', nächste fällig ' + befund.naechste : '') + '.';
        if (befund.ueberfaellig && intervall.in_ordnung !== false) {
          onPruefpunktChange(intervall, { wert_auswahl: 'n.i.O.', in_ordnung: false, bemerkung: text });
        } else if (!befund.ueberfaellig && intervall.in_ordnung !== true) {
          onPruefpunktChange(intervall, { wert_auswahl: 'i.O.', in_ordnung: true, bemerkung: text });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matten, raeume, schilder?.summe, ek?.summe, modul?.pruefpunkte, aktiv]);
  return null;
}

// === Sync-Komponente: US-Anschluss ohne Erdungsbolzen -> 04.3 n.i.O. ==========
// Anna-Lena 18.09.2026: "immer wenn ohne Erdungsbolzen = Mangel". Bei Arbeiten
// am Trafo muss nach DIN VDE 0105-100 geerdet werden koennen. Gleiches Muster wie
// ErderMangelSync: nur wenn sich der Befund aendert, darf die Automatik schreiben;
// der Monteur kann jederzeit von Hand ueberschreiben.
const AUTO_TEXT_ERDUNGSBOLZEN = 'Auto-Bewertung: US-Anschluss ohne Erdungsbolzen.';
function TrafoErdungSync({ trafo, modul, onPruefpunktChange, aktiv }) {
  const us = trafo?.us_anschluss || null;
  useEffect(() => {
    // aktiv = offenes Protokoll. Sonst haette ein spaeteres Oeffnen durchs Buero
    // ein freigegebenes Protokoll nachtraeglich veraendert.
    if (!aktiv || !modul?.pruefpunkte || !onPruefpunktChange) return;
    const pp = modul.pruefpunkte.find(x => String(x.pos_nummer || '').trim() === '04.3');
    if (!pp) return;
    const ohne = US_OHNE_ERDUNGSBOLZEN.includes(us);
    const hatAutoText = String(pp.bemerkung || '').startsWith('Auto-Bewertung:');
    // Befund IMMER festhalten (auch "offen") - sonst galt ein spaeterer Wechsel auf
    // "ohne Erdungsbolzen" als Erstbefund und schrieb nichts, wenn 04.3 schon
    // beantwortet war (Review 18.09.2026).
    const darf = autoDarfSchreiben(pp, ohne ? 'nicht_ok' : 'offen');
    if (ohne) {
      if (!darf || pp.in_ordnung === false) return;
      onPruefpunktChange(pp, {
        wert_auswahl: 'n.i.O.', in_ordnung: false, wert_modifiers: ['fehlt'],
        bemerkung: pp.bemerkung && !hatAutoText ? pp.bemerkung : AUTO_TEXT_ERDUNGSBOLZEN,
      });
    } else if (darf && hatAutoText && pp.in_ordnung === false) {
      // Erdungsbolzen doch vorhanden: die eigene Auto-Bewertung zuruecknehmen,
      // der Monteur beantwortet den Punkt dann selbst.
      onPruefpunktChange(pp, { wert_auswahl: null, in_ordnung: null, wert_modifiers: [], bemerkung: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [us, modul?.pruefpunkte]);
  return null;
}

// === Oelwanne: Ausfuehrung (05.1.1, Migration 54) ===========================
// "Fertigstation mit Anstrich": kein Abmessen, Auffangvolumen gilt als i.O.
// (Anna-Lena 18.09.2026). Wanne aus Blech/Kunststoff/sonstigem: abmessen,
// 05.1.5 bewertet die App aus den Massen (OelwannenMangelSync).
export const WANNE_ANSTRICH = 'Fertigstation mit Anstrich';
function wannenAusfuehrung(modul) {
  const pp = (modul?.pruefpunkte || []).find(x => String(x.pos_nummer || '').trim() === '05.1.1');
  return pp?.wert_auswahl || null;
}
function AnstrichVolumenSync({ modul, onPruefpunktChange, aktiv }) {
  const ausfuehrung = wannenAusfuehrung(modul);
  useEffect(() => {
    if (!aktiv || !modul?.pruefpunkte || !onPruefpunktChange || ausfuehrung !== WANNE_ANSTRICH) return;
    const pp = modul.pruefpunkte.find(x => String(x.pos_nummer || '').trim() === '05.2.5');
    if (!pp || !autoDarfSchreiben(pp, 'ok') || pp.in_ordnung === true) return;
    onPruefpunktChange(pp, { wert_auswahl: 'i.O.', in_ordnung: true,
      bemerkung: pp.bemerkung || 'Auto-Bewertung: Fertigstation mit Anstrich – Auffangraum durch die Bauart gegeben.' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ausfuehrung, modul?.pruefpunkte]);
  return null;
}

// === Sync-Komponente: schreibt Bewertung in Prüfpunkt 05.1.5 ================
// "Auffangvolumen auf Menge Trafoöl ausgelegt" wird automatisch auf
// n.i.O. gesetzt, wenn mindestens ein Öl-Trafo eine zu kleine Wanne hat.
// savePruefpunkt erstellt dann automatisch einen Mangel.
function OelwannenMangelSync({ trafos, modul, onPruefpunktChange, aktiv }) {
  useEffect(() => {
    if (!aktiv || !modul?.pruefpunkte || !onPruefpunktChange) return;

    const oelTrafos = (trafos || []).filter(t => t.trafoart && t.trafoart !== 'giessharz');
    const alleBewertungen = oelTrafos.map(t => computeOelwannenBewertung(t));
    const bewertungen = alleBewertungen.filter(b => b !== null);
    if (bewertungen.length === 0) return;

    const anyNichtOk = bewertungen.some(b => b?.status === 'nicht_ok');
    // "i.O." nur, wenn WIRKLICH jeder Oel-Trafo bewertet und ok ist. Vorher
    // zaehlte ein Oel-Trafo ohne erfasste Wanne gar nicht mit - Trafo A ok,
    // Trafo B ohne Wanne ergab automatisch i.O.
    const allOk = alleBewertungen.length > 0 && alleBewertungen.every(b => b?.status === 'ok');

    const pp515 = modul.pruefpunkte.find(pp => pp.pos_nummer === '05.1.5');
    if (!pp515) return;

    const ziel = anyNichtOk ? 'nicht_ok' : allOk ? 'ok' : 'offen';
    if (!autoDarfSchreiben(pp515, ziel)) return;

    if (ziel === 'nicht_ok' && pp515.in_ordnung !== false) {
      onPruefpunktChange(pp515, {
        wert_auswahl: 'n.i.O.',
        in_ordnung: false,
        bemerkung: pp515.bemerkung || 'Auto-Bewertung: Auffangvolumen reicht nicht für die Trafoöl-Menge.'
      });
    } else if (ziel === 'ok' && pp515.in_ordnung !== true) {
      onPruefpunktChange(pp515, {
        wert_auswahl: 'i.O.',
        in_ordnung: true,
        // Automatische Bemerkung passt nicht mehr zu i.O. - weg damit.
        ...(String(pp515.bemerkung || '').startsWith('Auto-Bewertung:') ? { bemerkung: null } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafos, modul?.pruefpunkte]);

  return null;
}

// === Erderanlage: Prüfpunkt 06.3 automatisch bewerten =======================
// 06.3 "Erdungswiderstandsmessung" hat den Sollwert "Messergebnis muss ≤ 2,0 Ω".
// Genau das steht als Ohm-Wert an jedem Anschluss der Schiene. Also bewertet die
// App den Punkt selbst, statt den Monteur dieselbe Zahl zweimal beurteilen zu
// lassen. Gleiches Muster wie OelwannenMangelSync bei Prüfpunkt 05.1.5:
// n.i.O. legt über savePruefpunkt automatisch einen Mangel an.
//
// Vorsichtsregeln:
// - Ohne einen einzigen gemessenen Wert wird nichts gesetzt.
// - Der Monteur kann jederzeit von Hand überschreiben; erst wenn sich der
//   Messbefund wieder ändert, greift die Automatik erneut.
function ErderMangelSync({ befund, modul, onPruefpunktChange, bereit, aktiv }) {
  useEffect(() => {
    if (!aktiv || !modul?.pruefpunkte || !onPruefpunktChange || !befund) return;
    // Erst bewerten, wenn die Stationskonfiguration da ist: sonst wuerde mit
    // dem Standard 2 Ω bewertet, obwohl die Station z. B. 1 Ω verlangt.
    if (!bereit) return;
    if (befund.anzahlGemessen === 0) return;   // noch nichts gemessen

    const pp63 = modul.pruefpunkte.find(pp => pp.pos_nummer === '06.3');
    if (!pp63) return;

    const ohmText = (n) => String(n).replace('.', ',');
    const ziel = befund.anzahlUeberGrenze > 0 ? 'nicht_ok' : 'ok';
    const darfSchreiben = autoDarfSchreiben(pp63, ziel);

    const autoText = 'Auto-Bewertung: ' + (befund.maxOl ? 'OL (über Messbereich)' : ohmText(befund.maxOhm) + ' Ω')
      + ' an "' + befund.schlechtesterAnschluss
      + '" über Grenzwert ' + ohmText(befund.grenze ?? 2) + ' Ω (' + befund.anzahlUeberGrenze + ' von ' + befund.anzahlGemessen + ' Anschlüssen).';
    const hatAutoText = String(pp63.bemerkung || '').startsWith('Auto-Bewertung:');
    // Messwert korrigiert, bleibt aber ueber der Grenze: NUR der Text muss nachziehen.
    // Das lief bisher nie, weil die Sperre oben schon zurueckkehrte - im PDF stand
    // dann der alte Ohm-Wert (Pruefung 20.09.2026).
    if (ziel === 'nicht_ok' && pp63.in_ordnung === false && hatAutoText && pp63.bemerkung !== autoText) {
      onPruefpunktChange(pp63, { bemerkung: autoText });
      return;
    }
    if (!darfSchreiben) return;
    if (ziel === 'nicht_ok' && pp63.in_ordnung !== false) {
      onPruefpunktChange(pp63, {
        wert_auswahl: 'n.i.O.',
        in_ordnung: false,
        bemerkung: pp63.bemerkung && !hatAutoText ? pp63.bemerkung : autoText,
      });
    } else if (ziel === 'ok' && pp63.in_ordnung !== true) {
      onPruefpunktChange(pp63, {
        wert_auswahl: 'i.O.',
        in_ordnung: true,
        ...(hatAutoText ? { bemerkung: null } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [befund, modul?.pruefpunkte, bereit, aktiv]);

  return null;
}

function ErderanlageMitBewertung({ station, config, modul, onPruefpunktChange, onStationReload, protokollOffen }) {
  const [befund, setBefund] = useState(null);
  // Aendert das Buero einen Messwert in einem abgeschlossenen Protokoll (z. B.
  // 99999 -> OL, Anna-Lena 21.09.2026), muessen 06.3 und der Mangeltext mitziehen.
  // Nur OEFFNEN aendert dagegen nichts.
  const [messwertGeaendert, setMesswertGeaendert] = useState(false);
  const stationId = station?.id;
  // Grenzwert gehoert zur Stationskonfiguration - Speichern ueber die Warteschlange.
  const conf = useAutoSave(mitWartendem('konfig', config || { station_id: stationId }, 'station_id'), async (next, aenderungen) => {
    if (!stationId) return null;
    const r = await zeileSpeichern('konfig', stationId, { station_id: stationId, ...aenderungen });
    if (r.status === 'gesendet' && onStationReload) onStationReload();
    return r;
  });
  return (
    <>
      <ErderanlageBlock
        stationId={stationId}
        protokollId={modul?.protokoll_id}
        onBefund={setBefund}
        grenzeOhm={conf.data?.erder_grenzwert_ohm}
        grenzeAendern={stationId ? (v) => conf.update({ erder_grenzwert_ohm: v }) : undefined}
        onMessungGeaendert={() => setMesswertGeaendert(true)}
      />
      <ErderMangelSync befund={befund} modul={modul} onPruefpunktChange={onPruefpunktChange} bereit={!!config}
        aktiv={protokollOffen || messwertGeaendert}/>
    </>
  );
}

// === Fotos von Typenschild und Schaltplan (Anna-Lena 21.09.2026) ===========
function TypenschildFotos({ stationId, protokollId, ms, trafo }) {
  const { user } = useAuth();
  if (ms) {
    return (
      <div style={{ marginBottom: 16 }}>
        <FotoFeld label={'📷 ' + t('Typenschild(-er) MS-Anlage')} kategorie='typenschild_ms'
          titel='Typenschild MS-Anlage' stationId={stationId} protokollId={protokollId} userId={user?.id}/>
        <FotoFeld label={'📷 ' + t('MS-Übersichtsschaltplan')} kategorie='schaltplan_ms'
          titel='MS-Übersichtsschaltplan' stationId={stationId} protokollId={protokollId} userId={user?.id}/>
      </div>
    );
  }
  if (!trafo?.id) return null;
  const name = trafo.bezeichnung || 'Trafo';
  return (
    <div style={{ marginBottom: 16 }}>
      <FotoFeld label={'📷 ' + t('Typenschild(-er) {name}', { name })} kategorie={'typenschild_trafo:' + trafo.id}
        titel={'Typenschild ' + name} stationId={stationId} protokollId={protokollId} userId={user?.id}/>
    </div>
  );
}

// === Dispatcher: zeigt das richtige Editor-Set basierend auf Modul-Code =====
// protokollOffen: false bei abgegebenen/freigegebenen Protokollen - dann schreiben
// die Automatiken nichts mehr in Stammdaten oder Pruefpunkte (Review 18.09.2026).
export function EditableStammdaten({ modul, station, config, msFelder, trafos, wandler, onStationReload, stationFehler, onPruefpunktChange, protokollOffen = true }) {
  const code = modul?.modul_vorlagen?.code;
  if (!code) return null;
  // Das Protokoll oeffnet seit v54, ohne auf die Stationsdaten zu warten.
  // Solange sie fehlen, lieber ein ruhiger Hinweis als ein leerer Block (oder
  // "Station zuerst speichern" bei der Erderanlage).
  if (!station && ['01-STATIONSKOERPER', '02-MS-ANLAGE', WANDLER_ZAEHLER_CODE, '04-TRAFO-SICHT', '05-OEL-WANNE', '06-ERDERANLAGE'].includes(code)) {
    // Laden gescheitert (kein Netz, noch nie auf diesem Geraet geoeffnet):
    // nicht ewig "wird geladen" zeigen, sondern sagen, was los ist.
    if (stationFehler) {
      return (
        <div style={{ padding: 12, background: C.yellowBg, color: C.navy, borderRadius: 8, marginBottom: 16, fontSize: 15,
                      display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            {t('Stammdaten der Station konnten nicht geladen werden (kein Netz?). Die Prüfpunkte unten lassen sich trotzdem ausfüllen.')}
          </span>
          {onStationReload && (
            <button type="button" onClick={() => onStationReload()}
              style={{ minHeight: 44, padding: '8px 14px', borderRadius: 7, border: '1.5px solid ' + C.navy, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>
              {t('Nochmal laden')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div style={{ padding: 12, background: C.gray100, color: C.gray500, borderRadius: 8, marginBottom: 16, fontSize: 15, fontStyle: 'italic' }}>
        {t('Stammdaten der Station werden geladen … (die Prüfpunkte unten lassen sich schon ausfüllen)')}
      </div>
    );
  }

  if (code === '01-STATIONSKOERPER') {
    return (
      <>
        <EditableKundeCard kundeId={station?.kunde_id} onSaved={onStationReload}/>
        <EditableStationCard station={station} config={config} onSavedStation={onStationReload} onSavedConfig={onStationReload}/>
      </>
    );
  }
  if (code === '02-MS-ANLAGE') {
    return (
      <>
        <EditableMsAnlage config={config} msFelder={msFelder} stationId={station?.id} protokollId={modul?.protokoll_id} onChanged={onStationReload}/>
        <TypenschildFotos stationId={station?.id} protokollId={modul?.protokoll_id} ms/>
      </>
    );
  }
  if (code === WANDLER_ZAEHLER_CODE) {
    return <EditableWandlerZaehler config={config} wandler={wandler} stationId={station?.id} protokollId={modul?.protokoll_id} onChanged={onStationReload}/>;
  }
  if (code === '04-TRAFO-SICHT') {
    const trafo = (trafos || []).find(t => t.id === modul.bezugs_id);
    if (!trafo) {
      return <div style={{ padding: 12, background: C.redBg, color: C.red, borderRadius: 8, marginBottom: 16, fontSize: 15 }}>{t('Trafo nicht mehr gefunden (eventuell gelöscht).')}</div>;
    }
    return (
      <>
        <TrafoErdungSync trafo={trafo} modul={modul} onPruefpunktChange={onPruefpunktChange} aktiv={protokollOffen}/>
        <EditableTrafoCard trafo={trafo} onSaved={onStationReload}/>
        <TypenschildFotos stationId={station?.id} protokollId={modul?.protokoll_id} trafo={trafo}/>
      </>
    );
  }
  if (code === '05-OEL-WANNE') {
    // Seit Migration 70 ein Kapitel je Trafo (bezugs_typ 'trafo'); alte Protokolle
    // haben noch ein Kapitel fuer alle Oel-Trafos der Station.
    const jeTrafo = modul?.bezugs_typ === 'trafo';
    const oelTrafos = (trafos || []).filter(t => t.trafoart && t.trafoart !== 'giessharz'
      && (!jeTrafo || t.id === modul.bezugs_id));
    if (oelTrafos.length === 0) {
      return <div style={{ padding: 12, background: C.gray100, color: C.gray500, borderRadius: 8, marginBottom: 16, fontSize: 15, fontStyle: 'italic' }}>{jeTrafo ? t('Bitte zuerst im Trafo-Kapitel die Trafo-Art eintragen – die Wanne wird nur bei Öl-Trafos abgemessen.') : t('Keine öl-gekuehlten Trafos in dieser Station.')}</div>;
    }
    // Format: ISO date → Tag.Monat.Jahr (deutsch)
    const formatDate = (iso) => {
      if (!iso) return null;
      try {
        const d = new Date(iso);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { return null; }
    };
    // Erst die Ausfuehrung waehlen (05.1.1); abgemessen wird nur eine Wanne.
    const ausfuehrung = wannenAusfuehrung(modul);
    // Keine Wanne (05.1 = nein) oder alter Stand 'nein' bei 05.1.1: nichts abmessen.
    const wanne051 = (modul?.pruefpunkte || []).find(x => String(x.pos_nummer || '').trim() === '05.1')?.wert_auswahl;
    if (wanne051 === 'nein' || ausfuehrung === 'nein') return null;
    if (ausfuehrung === WANNE_ANSTRICH) {
      return (
        <>
          <AnstrichVolumenSync modul={modul} onPruefpunktChange={onPruefpunktChange} aktiv={protokollOffen}/>
          <div style={{ padding: '10px 14px', background: C.gray100, borderLeft: '4px solid ' + C.gray300, borderRadius: 6, marginBottom: 14, fontSize: 14, color: C.gray700 }}>
            {t('Fertigstation mit Anstrich: kein Abmessen nötig – das Auffangvolumen gilt als in Ordnung.')}
          </div>
        </>
      );
    }
    if (!ausfuehrung) {
      return (
        <div style={{ padding: '10px 14px', background: C.gray100, borderLeft: '4px solid ' + C.gray300, borderRadius: 6, marginBottom: 14, fontSize: 14, color: C.gray700 }}>
          {t('Erst unten bei 05.1.1 die Ausführung wählen – bei einer Wanne erscheinen hier die Maße zum Abmessen.')}
        </div>
      );
    }
    return (
      <>
        <div style={{ padding: '10px 14px', background: C.orangeBg, borderLeft: '4px solid ' + C.orange, borderRadius: 6, marginBottom: 14, fontSize: 14, color: C.gray700 }}>
          <strong>⚠️ {t('Hinweis:')}</strong> {t('Maße und Werte hier werden direkt in die Stammdaten der Station übernommen und überschreiben den vorherigen Stand. Wenn die Bewertung rot wird, legt die App automatisch einen Mangel im Prüfpunkt 05.1.5 an.')}
        </div>
        <OelwannenMangelSync trafos={oelTrafos} modul={modul} onPruefpunktChange={onPruefpunktChange} aktiv={protokollOffen}/>
        {oelTrafos.map(t => (
          <EditableTrafoMitWanne
            key={t.id}
            trafo={t}
            lastUpdated={formatDate(t.geaendert_am || t.aktualisiert_am || station?.geaendert_am)}
            onSaved={onStationReload}
          />
        ))}
      </>
    );
  }
  if (code === '03-NSHV') {
    // Weitere Verteilungen (VT 2 ...) schreiben NICHT in nshv_vorhanden - das
    // gilt fuer die Haupt-NSHV (Anna-Lena 08.10.2026, Migration 76).
    if (modul?.bezugs_typ === 'verteilung') return null;
    return <NshvKonfigSync station={station} config={config} modul={modul} onStationReload={onStationReload} aktiv={protokollOffen && !!onPruefpunktChange}/>;
  }
  if (code === '05-SICHERHEITS-ZUBEHOER') {
    const soll = isoliermattenSoll(config);
    const schilder = schilderSoll(config, msFelder);
    const ek = ekSoll(config, msFelder, trafos);
    const loescherDatum = ((modul?.pruefpunkte || []).find(x => posVon(x) === '05.30')?.zusatz_werte || {}).letzte_pruefung;
    const loescher = loescherDatum ? feuerloescherBefund(loescherDatum) : null;
    const kasten = { padding: '8px 12px', background: C.gray100, borderLeft: '4px solid ' + C.gray300, borderRadius: 6, marginBottom: 8, fontSize: 14, color: C.gray700, lineHeight: 1.45 };
    return (
      <>
        <ZubehoerSync config={config} msFelder={msFelder} trafos={trafos} modul={modul} onPruefpunktChange={onPruefpunktChange} aktiv={protokollOffen}/>
        {ek && (
          <div style={kasten}>
            {t('Erdungs- und Kurzschließvorrichtungen soll: {n} Stück (Halterungen ebenso)', { n: ek.summe })}
            {' ('}{[ek.messfelder ? t('Messfelder {n}', { n: ek.messfelder }) : null,
                    ek.trafo ? t('Trafos {n}', { n: ek.trafo }) : null,
                    ek.abgaenge ? t('Abgänge ohne Erdungsschalter {n}', { n: ek.abgaenge }) : null].filter(Boolean).join(' + ') || '0'}{')'}
            {ek.schalterUnbekannt ? ' – ' + t('Luftanlage: bitte bei der MS-Anlage angeben, ob Erdungsschalter vorhanden sind.') : ''}
          </div>
        )}
        {soll != null && (
          <div style={kasten}>{t('Isoliermatten laut Räumen der Station: {n} Stück (je MS-, NS- und kombiniertem Raum eine).', { n: soll })}</div>
        )}
        {schilder && (
          <div style={kasten}>
            {t('Warn- und Hinweisschilder soll: {n} Stück', { n: schilder.summe })}
            {' ('}{t('{r} Raum/Räume × 4', { r: schilder.raeume })}
            {schilder.rueckspannung > 0 ? ' + ' + t('{n} × Vorsicht Rückspannung', { n: schilder.rueckspannung }) : ''}
            {schilder.feld > 0 ? ' + ' + t('{n} MS-Felder × 2 (nicht schalten / geerdet und kurzgeschlossen)', { n: schilder.felder }) : ''}{')'}
          </div>
        )}
        {loescher && (
          <div style={{ ...kasten, borderLeftColor: loescher.ueberfaellig ? C.red : C.green }}>
            {loescher.ueberfaellig
              ? t('Feuerlöscher: letzte Prüfung vor {m} Monaten – Prüfung überfällig (alle 2 Jahre).', { m: loescher.monate })
              : t('Feuerlöscher: nächste Prüfung fällig {datum}.', { datum: loescher.naechste || '–' })}
          </div>
        )}
        <div style={{ marginBottom: 6 }}/>
      </>
    );
  }
  if (code === '06-ERDERANLAGE') {
    return <ErderanlageMitBewertung station={station} config={config} modul={modul} onPruefpunktChange={onPruefpunktChange} onStationReload={onStationReload} protokollOffen={protokollOffen} />;
  }
  // Für andere Module (NSHV, Sicherheits-Zubehör, ...) kein Stammdaten-Block
  return null;
}
